import { timezoneZodSchema } from '@/lib/time-zones';
import { disallowedEmailDomains, internationalPhoneRegex } from '@/lib/string-utils';
import { CreateUserOptions } from '@airreading/common/data';
import { z } from 'zod';

export const createCoachSchema = z.object({
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  email: z
    .string()
    .email({ message: 'Invalid email format' })
    .refine((val) => !disallowedEmailDomains.some((domain) => val.toLowerCase().endsWith(domain)), {
      message: 'Email must be personal, not corporate',
    }),
  phone: z.string().min(1, { message: 'Phone is required' }).regex(internationalPhoneRegex, {
    message: 'Phone number must be in international format (e.g. +1, +54)',
  }),
  smsReminderOptedIn: z.boolean().default(true),
  timeZone: timezoneZodSchema,
  displayName: z.string().min(1, { message: 'Display name is required' }),
  secondaryEmail: z.string().email({ message: 'Invalid email format' }).optional().or(z.literal('')),
  iss: z.string().nullable().optional().default(null),
  sub: z.string().nullable().optional().default(null),
  isRegistrable: z.boolean().default(true),
});
export type CreateCoachFormSchema = z.infer<typeof createCoachSchema>;

export function createUserOptionsFromFormData({
  firstName,
  lastName,
  email,
  phone,
  timeZone,
  iss,
  sub,
  isRegistrable,
}: CreateCoachFormSchema): CreateUserOptions {
  if ((iss && !sub) || (!iss && sub)) {
    throw new TypeError('Both the Issuer Identifier and the Subject Identifier must be present.');
  }
  const sso = iss && sub ? { iss, sub } : null;

  return {
    firstName,
    lastName,
    email,
    phone,
    assignRoles: [],
    timeZone: timeZone.value,
    externalKeys: sso,
    isRegistrable,
  };
}
